import { type FileStatusId, isInternalUser } from '@steerfi/shared';
import { Download, Eye } from 'lucide-react';
import type { PaginatedPaymentFilesResponse, PaymentFileItem } from '@/api/file-upload-api';
import { getStatusColor, getStatusLabel } from '@/components/status-chip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { formatDate } from '@/lib/utils';
import { type ColumnConfig, DataTable } from './data-table';
import type { RowActivatorLink } from './row-activator';

interface Props {
  data?: PaginatedPaymentFilesResponse | undefined;
  /**
   * The table's accessible name. Threaded from the parent rather than hardcoded
   * because the two callers show these files under different names -- "Payment
   * files" on its own page, "Collections" on Escrow, where it sits beside the
   * disbursements table.
   */
  caption: string;
  isLoading?: boolean;
  error?: string | null;
  onFileClick?: (file: PaymentFileItem) => void;
  /** Detail route for the row's keyboard activator; omit to fall back to a button. */
  getRowLink?: ((file: PaymentFileItem) => RowActivatorLink) | undefined;
  onQuickView?: (file: PaymentFileItem) => void;
  onDownload?: (file: PaymentFileItem) => void;
  onFilterChange?: (filterKey: string, value: string | string[]) => void;
  onSortChange?: (sortBy: string, order: 'asc' | 'desc') => void;
  onPageChange?: (page: number) => void;
  /** Sort actually in effect, so the header arrow reflects the query. */
  currentSortKey?: string;
  currentSortOrder?: 'asc' | 'desc';
}

/**
 * Column keys `GET /api/payments/files` will sort by -- the `sortable: true`
 * columns below. The API 400s anything else, so a `sortBy` arriving from the URL
 * has to be checked against this before it reaches a request.
 */
export const PAYMENT_FILE_SORT_KEYS = [
  'file_id',
  'file_name',
  'client_name',
  'status_type_id',
  'collection_type',
  'file_size_bytes',
  'created_at',
] as const;

export const DEFAULT_PAYMENT_FILE_SORT_KEY = 'created_at';

export function readPaymentFileSortBy(raw: string | null | undefined, fallback: string): string {
  const value = raw?.trim() ?? '';
  return (PAYMENT_FILE_SORT_KEYS as readonly string[]).includes(value) ? value : fallback;
}

/**
 * Format file size in bytes to human-readable format
 */
const formatFileSize = (bytes: string | null): string => {
  if (!bytes) return 'N/A';
  const size = Number.parseInt(bytes, 10);
  if (Number.isNaN(size)) return 'N/A';

  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const PaymentFilesTable = ({
  data,
  caption,
  isLoading = false,
  error = null,
  onFileClick,
  getRowLink,
  onQuickView,
  onDownload,
  onSortChange,
  onPageChange,
  currentSortKey,
  currentSortOrder,
}: Props) => {
  const { user } = useKeycloakAuth();
  const showClientName = isInternalUser(user?.roles);

  const columns: ColumnConfig<PaymentFileItem>[] = [
    {
      key: 'file_id',
      label: 'File ID',
      sortable: true,
      render: (item) => (
        <span className="font-mono text-xs text-muted-foreground">
          {item.file_id.slice(0, 8)}...
        </span>
      ),
    },
    {
      key: 'file_name',
      label: 'File Name',
      sortable: true,
      render: (item) => (
        <span className="font-medium text-sm max-w-xs truncate block" title={item.file_name}>
          {item.file_name}
        </span>
      ),
    },
    {
      key: 'client_name',
      label: 'Client Name',
      sortable: true,
      render: (item) => (
        <span className="text-sm text-muted-foreground">{item.client_name || '—'}</span>
      ),
    },
    {
      // Not sortable: this is the primary account's reference, and unlike client
      // name and collection type the API has no sort key for it.
      key: 'debit_account_identifier',
      label: 'Debit Account',
      sortable: false,
      render: (item) => (
        <span className="text-sm text-muted-foreground font-mono">
          {item.debit_account_identifier || '—'}
        </span>
      ),
    },
    {
      key: 'status_type_id',
      label: 'Status',
      sortable: true,
      render: (item) => (
        <Badge variant={getStatusColor(item.status_type_id as FileStatusId, 'file')}>
          {getStatusLabel(item.status_type_id as FileStatusId, 'file')}
        </Badge>
      ),
    },
    {
      key: 'collection_type',
      label: 'Collection Type',
      sortable: true,
      render: (item) => (
        <span className="text-sm text-muted-foreground capitalize">
          {item.collection_type || '—'}
        </span>
      ),
    },
    {
      key: 'file_size_bytes',
      label: 'Size',
      sortable: true,
      render: (item) => (
        <span className="text-sm text-muted-foreground">
          {formatFileSize(item.file_size_bytes)}
        </span>
      ),
    },
    {
      key: 'payment_count',
      label: 'Payments',
      sortable: false,
      render: (item) => <span className="text-sm font-medium">{item.payment_count}</span>,
    },
    {
      key: 'total_payment_amount',
      label: 'Total Amount',
      sortable: false,
      render: (item) => (
        <span className="text-sm font-medium">
          {item.total_payment_amount
            ? `$${parseFloat(item.total_payment_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : 'N/A'}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Uploaded',
      sortable: true,
      render: (item) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(item.created_at.toString())}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (item) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (onQuickView) {
                onQuickView(item);
              } else {
                onFileClick?.(item);
              }
            }}
            title="Quick view"
            aria-label={`Quick view ${item.file_name}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onDownload?.(item);
            }}
            title="Download file"
            aria-label={`Download ${item.file_name}`}
          >
            <Download className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  const visibleColumns = showClientName
    ? columns
    : columns.filter((column) => column.key !== 'client_name');

  const mappedData = {
    data: data?.items || [],
    ...(data?.pagination && {
      pagination: {
        page: data.pagination.page,
        pageSize: data.pagination.limit,
        total: data.pagination.total,
        totalPages: data.pagination.totalPages,
      },
    }),
  };

  return (
    <DataTable
      data={mappedData}
      columns={visibleColumns}
      caption={caption}
      isLoading={isLoading}
      error={error}
      onRowClick={onFileClick}
      // The first column is a truncated id, so the file name carries the activator.
      rowActivatorColumnKey="file_name"
      getRowLabel={(item) => `Open payment file ${item.file_name}`}
      {...(getRowLink && { getRowLink })}
      onSortChange={onSortChange || (() => {})}
      onPageChange={onPageChange || (() => {})}
      // Driven by the page's filter state: this grid declared no default, so it
      // showed no arrow at all while the API was ordering by created_at desc.
      {...(currentSortKey && { currentSortKey })}
      {...(currentSortOrder && { currentSortOrder })}
    />
  );
};
